import { useEffect, useState, useRef } from 'react';
import { Button, Col, ControlLabel, FormControl, FormGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import * as moment from 'moment';
import { IAuditFieldLog, IActivityLogFilters } from '@nkr/store/center/actions-center-activity-log';

const renderReasonTooltip = (text: string) => <Tooltip id='activity-log-reason-tooltip'>{text}</Tooltip>;

const formatBool = (v: string) => {
    if (v === 'True' || v === '1') return 'Yes';
    if (v === 'False' || v === '0') return 'No';
    return v ?? '';
};

const getColumns = (): ColDef[] => [
    {
        field: 'changeDateTime',
        headerName: 'Date / Time',
        width: 155,
        valueFormatter: p => p.value ? moment.utc(p.value).local().format('MM/DD/YYYY h:mm A') : '',
        sort: 'desc'
    },
    { field: 'columnName', headerName: 'Field', flex: 2, minWidth: 160 },
    { field: 'beforeValue', headerName: 'Before', flex: 1, minWidth: 100 },
    { field: 'afterValue', headerName: 'After', flex: 1, minWidth: 100 },
    { field: 'userFullName', headerName: 'User', flex: 1, minWidth: 140 },
    {
        field: 'reason',
        headerName: 'Reason',
        flex: 2,
        minWidth: 120,
        cellRendererFramework: (params: ICellRendererParams) => {
            const text = params.value ?? '';
            if (!text) return '';
            const truncated = text.length > 30 ? text.substring(0, 30) + '...' : text;
            return (
                <OverlayTrigger
                    trigger='hover'
                    placement='left'
                    rootClose
                    overlay={renderReasonTooltip(text)}
                >
                    <span style={{ cursor: 'pointer' }}>{truncated}</span>
                </OverlayTrigger>
            );
        }
    }
];

interface IProps {
    show: boolean,
    centerId: number,
    onClose: () => void,
    getActivityLog: (centerId: number, filters: IActivityLogFilters) => Promise<any>
}

const ActivityLogModal = (props: IProps) => {
    const [rows, setRows] = useState<IAuditFieldLog[]>([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const gridApiRef = useRef<GridApi>(null);

    const load = () => {
        const filters: IActivityLogFilters = {
            fromDate: fromDate || undefined,
            toDate: toDate || undefined
        };
        props.getActivityLog(props.centerId, filters).then(response => {
            if (response.data.success) {
                setRows(response.data.results.map(r => ({
                    ...r,
                    beforeValue: formatBool(r.beforeValue),
                    afterValue: formatBool(r.afterValue)
                })));
            }
        });
    };

    useEffect(() => {
        if (props.show) load();
    }, [props.show]);

    const handleExport = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({
                fileName: `activity-log-center-${props.centerId}-${moment().format('YYYY-MM-DD')}.csv`
            });
        }
    };

    const onGridReady = (e: GridReadyEvent) => {
        gridApiRef.current = e.api;
    };

    return (
        <Modal bsSize='large' show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Activity Log</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col xs={5}>
                        <FormGroup>
                            <ControlLabel>From Date</ControlLabel>
                            <FormControl componentClass='input' type='date' value={fromDate} onChange={(e: any) => setFromDate(e.target.value)} />
                        </FormGroup>
                    </Col>
                    <Col xs={5}>
                        <FormGroup>
                            <ControlLabel>To Date</ControlLabel>
                            <FormControl componentClass='input' type='date' value={toDate} onChange={(e: any) => setToDate(e.target.value)} />
                        </FormGroup>
                    </Col>
                    <Col xs={2} style={{ paddingTop: 25 }}>
                        <Button bsStyle='primary' onClick={load}>Filter</Button>
                    </Col>
                </Row>

                <div style={{ height: 450 }} className='ag-theme-balham'>
                    <AgGridReact
                        columnDefs={getColumns()}
                        rowData={rows}
                        onGridReady={onGridReady}
                        defaultColDef={{ sortable: true, resizable: true }}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='default' onClick={handleExport}>Export CSV</Button>
                <Button onClick={props.onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ActivityLogModal;
